/**
 * The Lissajous figures the loading beam can trace (Settings > Appearance).
 *
 * Each is x = cos(a·t), y = sin(b·t). `a` is always odd: with an even `a` and
 * an odd `b` the curve doubles back on itself and draws only half a figure.
 * 3:2 is the mark. The busier ratios (5:6, 7:6, …) are left out because they
 * turn into a mesh at loader size.
 */
export interface LoadingFigure {
  id: string
  a: number
  b: number
  name: string
}

export const LOADING_FIGURES = [
  { id: '3:2', a: 3, b: 2, name: 'The mark' },
  { id: '1:2', a: 1, b: 2, name: 'Figure eight' },
  { id: '1:3', a: 1, b: 3, name: 'Triple loop' },
  { id: '3:4', a: 3, b: 4, name: 'Lattice' },
  { id: '5:4', a: 5, b: 4, name: 'Weave' },
  { id: '5:2', a: 5, b: 2, name: 'Ribbon' },
] as const satisfies readonly LoadingFigure[]

type LoadingFigureId = (typeof LOADING_FIGURES)[number]['id']
/**
 * Not a Lissajous figure: the Selectric's type element, two fans of spokes
 * turning against each other as a moiré (utils/moire.ts, "typeball").
 */
export const TYPEBALL = 'typeball'
/** A figure, the typeball, or a different one each time the loader appears. */
export type LoadingFigureSetting = LoadingFigureId | typeof TYPEBALL | 'random'

export const DEFAULT_LOADING_FIGURE: LoadingFigureSetting = '3:2'
const THE_MARK: LoadingFigure = LOADING_FIGURES[0]

export function normalizeLoadingFigure(value: unknown): LoadingFigureSetting {
  return value === 'random' || value === TYPEBALL || LOADING_FIGURES.some((f) => f.id === value)
    ? (value as LoadingFigureSetting)
    : DEFAULT_LOADING_FIGURE
}

/** What to draw for a setting: a figure, or the typeball. 'random' picks one now. */
export function resolveLoadingFigure(setting: LoadingFigureSetting): LoadingFigure | typeof TYPEBALL {
  if (setting === 'random') {
    const i = Math.floor(Math.random() * (LOADING_FIGURES.length + 1))
    return LOADING_FIGURES[i] ?? TYPEBALL
  }
  if (setting === TYPEBALL) return TYPEBALL
  return LOADING_FIGURES.find((f) => f.id === setting) ?? THE_MARK
}

/*
 * Line width. The mark's stroke follows an oscilloscope beam's physics: it
 * glows thicker where the beam moves slowly. Fitted to the mark's source
 * (designs/raw/svg-inline/mark.svg), its width in mark units is
 * 2.81 + 20.6 / speed, to within 0.3 on average. Other figures use the same
 * rule on their speed relative to their own average, so each carries the
 * mark's weight; their slowest points are capped at the mark's thickest, and
 * busier figures are drawn a little finer. For 3:2 all three reduce to the
 * mark's own rule.
 */
const MARK_MEAN_SPEED = 2.433
const MARK_SLOWEST = 0.93 / MARK_MEAN_SPEED // the mark's slowest point, relative to its average

function speed(f: LoadingFigure, t: number) {
  return Math.hypot(f.a * Math.sin(f.a * t), f.b * Math.cos(f.b * t))
}

/** Returns the figure's width along t, in the mark's 216-unit grid. */
export function figureWidth(f: LoadingFigure): (t: number) => number {
  let mean = 0
  const n = 2000
  for (let i = 0; i < n; i++) mean += speed(f, (2 * Math.PI * i) / n)
  mean /= n
  const fineness = Math.sqrt((THE_MARK.a + THE_MARK.b) / (f.a + f.b))
  return (t) => {
    const relative = Math.max(speed(f, t) / mean, MARK_SLOWEST)
    return (2.81 + 20.6 / (relative * MARK_MEAN_SPEED)) * fineness
  }
}
